import { ContractVersionInsightFragment } from 'generated/resources/graphql'
import moment from 'moment'

// Types for both compensation systems
type NewAdditionalCompensation =
  ContractVersionInsightFragment['additionalCompensationsConnection']['nodes'][number]

type LegacyCompensation = ContractVersionInsightFragment['compensationsConnection']['nodes'][number]

export const CENTS = 100

export const MONTHS_PER_YEAR = 12

// Legacy compensations: only consider fixed type
const LEGACY_FIXED_COMPENSATION_TYPE = 'fixed'

// New compensations: only consider fixed amount strategy
const FIXED_AMOUNT_STRATEGY_TYPE = 'contracts_fixed_amount_strategy'

export const getXTicksFromMonth = (startMonth: string, currentDate: string) => {
  const now = moment(currentDate).endOf('month') // Current date at the start of the current month
  const end = moment(startMonth, 'YYYY-MM-DD').endOf('month') // Start date at the start of the month

  const ticks = []

  // If the start date is within the last 12 months, adjust it to show the last 12 months
  const diffMonths = now.diff(end, 'months')
  if (diffMonths < 12) {
    end.subtract(12 - diffMonths, 'months') // Go back to cover at least 12 months
  }

  // Generate ticks at a 1-month interval from the adjusted start date to the current month
  const currentTick = moment(end)
  while (currentTick.isSameOrBefore(now)) {
    ticks.push(currentTick.format('YYYY-MM-DD'))
    currentTick.add(1, 'months') // Move to the next month
  }

  return ticks
}

export const getContractVersionInMonth = (
  contractVersions: ContractVersionInsightFragment[],
  month: string
) => {
  // Convert the input month string to a moment date at the start of the month
  const targetMonth = moment(month, 'YYYY-MM-DD').endOf('month')

  // Find the latest version whose effectiveOn is on or before the target month
  const contractVersion = contractVersions
    .slice()
    .reverse()
    .find((contract) => {
      const effectiveOn = moment(contract.effectiveOn, 'YYYY-MM-DD')

      return targetMonth.isSameOrAfter(effectiveOn)
    })

  // Return the found contract version
  return contractVersion
}

const getNewRecurrenceCount = (compensation: NewAdditionalCompensation): number => {
  if (!compensation.recurrence) {
    return 1
  }
  if (compensation.recurrence === 'monthly') {
    return 1
  }
  return parseInt(compensation.recurrence?.split('_')[1] ?? '1', 10)
}

const shouldShowNewCompensationInMonth = (
  compensation: NewAdditionalCompensation,
  monthDate: string,
  contractEffectiveOn: string
): boolean => {
  const firstPaymentDate = compensation.firstPaymentOn
    ? moment(compensation.firstPaymentOn)
    : moment(contractEffectiveOn)

  const targetMonth = moment(monthDate).startOf('month')
  const firstPaymentMonth = firstPaymentDate.clone().startOf('month')

  if (targetMonth.isBefore(firstPaymentMonth)) {
    return false
  }
  if (targetMonth.isSame(firstPaymentMonth)) {
    return true
  }

  const recurrenceInMonths = getNewRecurrenceCount(compensation)
  const monthsSinceFirstPayment = targetMonth.diff(firstPaymentMonth, 'months')

  // Check if the months since first payment is a multiple of the recurrence in months
  // This is to determine if the compensation should be shown in the month
  return monthsSinceFirstPayment % recurrenceInMonths === 0
}

const getNewCompensationAmount = (compensation: NewAdditionalCompensation): number | null => {
  const amountStrategy = compensation.amountStrategy
  if (!amountStrategy) return null
  if ('amount' in amountStrategy) {
    return amountStrategy.amount ? Number(amountStrategy.amount) : null
  }
  return null
}

export const newCompensationsAmount = (
  contractVersion: ContractVersionInsightFragment,
  monthDate: string
) => {
  return contractVersion.additionalCompensationsConnection.nodes.reduce((acc, compensation) => {
    if (compensation.amountStrategyType !== FIXED_AMOUNT_STRATEGY_TYPE) {
      return acc
    }
    if (!shouldShowNewCompensationInMonth(compensation, monthDate, contractVersion.effectiveOn)) {
      return acc
    }
    const amount = getNewCompensationAmount(compensation)
    if (amount) {
      const recurrenceInMonths = getNewRecurrenceCount(compensation)
      return acc + amount / recurrenceInMonths
    }
    return acc
  }, 0)
}

const getLegacyRecurrenceCount = (compensation: LegacyCompensation): number => {
  // When syncWithSupplements is true, use recurrenceCount
  if (compensation.syncWithSupplements) {
    return compensation.recurrenceCount ?? 1
  }

  if (!compensation.recurrence) {
    return 1
  }
  if (compensation.recurrence === 'monthly') {
    return 1
  }
  return parseInt(compensation.recurrence?.split('_')[1] ?? '1', 10)
}

const shouldShowLegacyCompensationInMonth = (
  compensation: LegacyCompensation,
  monthDate: string,
  contractEffectiveOn: string
): boolean => {
  const firstPaymentDate = compensation.firstPaymentOn
    ? moment(compensation.firstPaymentOn)
    : moment(contractEffectiveOn)

  const targetMonth = moment(monthDate).startOf('month')
  const firstPaymentMonth = firstPaymentDate.clone().startOf('month')

  if (targetMonth.isBefore(firstPaymentMonth)) {
    return false
  }
  if (targetMonth.isSame(firstPaymentMonth)) {
    return true
  }

  const recurrenceInMonths = getLegacyRecurrenceCount(compensation)
  const monthsSinceFirstPayment = targetMonth.diff(firstPaymentMonth, 'months')
  return monthsSinceFirstPayment % recurrenceInMonths === 0
}

export const legacyCompensationsAmount = (
  contractVersion: ContractVersionInsightFragment,
  monthDate: string
) => {
  return contractVersion.compensationsConnection.nodes.reduce((acc, compensation) => {
    // Only consider fixed type compensations
    if (compensation.compensationType !== LEGACY_FIXED_COMPENSATION_TYPE) {
      return acc
    }
    if (
      !shouldShowLegacyCompensationInMonth(compensation, monthDate, contractVersion.effectiveOn)
    ) {
      return acc
    }

    const { amount, yearlyEquivalentAmount } = compensation

    if (typeof yearlyEquivalentAmount === 'number' || typeof yearlyEquivalentAmount === 'string') {
      return acc + Number(yearlyEquivalentAmount) / MONTHS_PER_YEAR
    }

    if (typeof amount === 'number' || typeof amount === 'string') {
      const recurrenceInMonths = getLegacyRecurrenceCount(compensation)
      return acc + Number(amount) / recurrenceInMonths
    }

    return acc
  }, 0)
}
